import type { RawProductQuery } from '@packages/gql/generated/shopping/graphql';
import type { EnergieEfficiencyFlag, TireEfficiencyFlag } from '../types';
import { tireProductToUiTypeMap } from './mapping';

/**
 * Converts raw GraphQL power efficiency flags into the local efficiency flag model.
 *
 * Maps shared properties and narrows `hasOldLabel` to literal `true` / `false`
 * so the result matches the discriminated union `EnergieEfficiencyFlag`.
 * Returns `null` when no source data is available.
 */
export const convertProductEnergyEfficiencyData = (
  data: NonNullable<NonNullable<RawProductQuery['product']>['powerEfficiencyFlags']>,
): EnergieEfficiencyFlag[] =>
  data.map((flag) => {
    const baseFlag = {
      level: flag.level,
      dataSheetUrl: flag.dataSheetKeys[0] || undefined,
      linkToEnergieEfficiencyLabel: flag.link,
      flagDescription: flag.text || undefined,
    };

    return flag.hasOldLabel
      ? {
          ...baseFlag,
          hasOldLabel: true,
          color: flag.color,
        }
      : {
          ...baseFlag,
          hasOldLabel: false,
        };
  });

/**
 * Converts raw GraphQL tire efficiency data into the local tire efficiency model.
 *
 * Maps each tire flag type to the UI type via `tireProductToUiTypeMap` and
 * returns `null` when tire efficiency data is missing.
 */
export const convertProductTireEfficiencyData = (
  data: NonNullable<NonNullable<RawProductQuery['product']>['tireEfficiency']>,
): TireEfficiencyFlag => ({
  linkToEnergieEfficiencyLabel: data.link,
  flags: data.flags.map((flag) => ({
    level: flag.level,
    type: tireProductToUiTypeMap[flag.type],
  })),
});

export const convertProductDownloadsData = (
  data: NonNullable<NonNullable<RawProductQuery['product']>['downloads']>,
): string[] =>
  data.filter((download) => download.type === 'PRODUCT_DATASHEET').map((download) => download.link);
